import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RiAlertFill } from 'react-icons/ri';
import { OrderingSelectors, OrderingOperations, OrderingConstants } from 'polygon-ordering';
import getInvalidOfferPresent from '../selectors/getInvalidOfferPresent';
import getInvalidStagedPurchasePresent from '../selectors/getInvalidStagedPurchasePresent';
import combineStyles from '../utils/combineStyles';
import StandardButton from './StandardButton';
import PaymentRequestButton from './PaymentRequestButton';
import lodash from 'lodash';
import { useAppSelector, useAppDispatch } from '../app/hooks';
import getCurrentSavedCardToken from '../selectors/getCurrentSavedCardToken';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { enqueueErrorSnackbar } from '../utils/snackbar';
import { validate } from '../thunks/validate';
import { refreshOrderingProviderDetails } from '../thunks/refreshOrderingProviderDetails';
import { loadStripe } from '@stripe/stripe-js';
import { setCheckoutInProgress } from '../slices/checkoutInProgress';
import getProfile from '../selectors/getProfile';
import { useTyro } from '../hooks/TyroProvider';
import { useRedcatPayment } from '../shared/redcatpayments/hooks/useRedcatPayment';
import { processId } from '../screens/ReviewOrderScreen';
import { DeliveryAddressDto } from '../libs/polygon-ordering/src/dtos/DeliveryAddressDto';
import { applyTokenToGatewayPaymentMethod } from '../thunks/applyTokenToGatewayPaymentMethod';
import { trackPaymentFailed, trackPaymentInitiated, useRedcatAnalytics } from '../analytics';

const {
  getOrderTotals,
  getOrderReadyToSubmit,
  getAvailablePaymentMethods,
  getSelectedPaymentMethods,
  getTotalCoveredBySubPayments,
  getCartContainsSoldOutItems,
  getCartContainsInvalidQuantities,
  getFreeOrderReadyToSubmit,
  getShowMaxValue,
  getPurchaser,
  getPaymentGatewayConfig,
  getMember,
  getEnableDynamicPaymentGatewayConfig,
  getGuestOrderingAvailable,
  getEnableStripeCvcRevalidation,
  getBypassDeliveryDetails,
  getGatewayType,
  getLocationId,
} = OrderingSelectors;

const {
  addSelectedPaymentMethod,
  resetSelectedPaymentMethods,
  updateSelectedPaymentMethods,
  sale,
  finaliseSale,
  chargeRedcatPaySavedCard,
} = OrderingOperations;

const { PAYMENT_GATEWAY_TYPES, PAYMENT_METHOD, TYRO_PAYMENT_TOKEN_STATUS } = OrderingConstants;

const PAYMENT_GATEWAY_METHODS = [PAYMENT_METHOD.CREDIT_CARD, PAYMENT_METHOD.SAVED_CARD];

interface IProps {
  checkoutSuccessful: (id?: string) => void;
  paymentStatusPolling?: boolean;
  containerStyle?: React.CSSProperties;
}

const CheckoutButton: React.FC<IProps> = ({
  checkoutSuccessful,
  paymentStatusPolling,
  containerStyle,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const enableDynamicPaymentGatewayConfig = useAppSelector(getEnableDynamicPaymentGatewayConfig);
  const member = useAppSelector(getMember);
  const gatewayType = useAppSelector(getGatewayType);
  const paymentGatewayConfig = useAppSelector(getPaymentGatewayConfig);
  const orderReadyToSubmit = useAppSelector(getOrderReadyToSubmit);
  const availablePaymentMethods = useAppSelector(getAvailablePaymentMethods);
  const selectedPaymentMethods = useAppSelector(getSelectedPaymentMethods);
  const invalidOfferPresent = useAppSelector(getInvalidOfferPresent);
  const invalidStagedPurchasePresent = useAppSelector(getInvalidStagedPurchasePresent);
  const totalCoveredBySubPayments = useAppSelector(getTotalCoveredBySubPayments);
  const creditCardComplete = useAppSelector(state => state.creditCardComplete);
  const checkoutInProgress = useAppSelector(state => state.checkoutInProgress);
  const cartContainsSoldOutItems = useAppSelector(getCartContainsSoldOutItems);
  const cartContainsInvalidQuantities = useAppSelector(getCartContainsInvalidQuantities);
  const freeOrderReadyToSubmit = useAppSelector(getFreeOrderReadyToSubmit);
  const enableCvcCheck = useAppSelector(getEnableStripeCvcRevalidation);
  const cvcFilled = useAppSelector(state => state.cvcFilled);
  const currentSavedCardToken = useAppSelector(getCurrentSavedCardToken);

  // eslint-disable-next-line
  const elements = gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE ? useElements() : null;
  // eslint-disable-next-line
  const stripe = gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE ? useStripe() : null;

  const orderTotals = useAppSelector(getOrderTotals);
  const locationId = useAppSelector(getLocationId);
  const maxValueReached = useAppSelector(getShowMaxValue);
  const payment_gateway = selectedPaymentMethods.filter(options =>
    PAYMENT_GATEWAY_METHODS.includes(options.method),
  )[0];
  const purchaser = useAppSelector(getPurchaser);
  const profile = useAppSelector(getProfile);
  const guestOrderingAvailable = useAppSelector(getGuestOrderingAvailable);
  const maxOrderValueReached = useAppSelector(getShowMaxValue);
  const isMultiPaymentAllowed = useAppSelector(state => state.config.multiPaymentsAllowed);
  const payingWithGoogleOrAppleOrCreditCard = selectedPaymentMethods.some(({ method }) =>
    [PAYMENT_METHOD.APPLE_PAY, PAYMENT_METHOD.GOOGLE_PAY, PAYMENT_METHOD.CREDIT_CARD].includes(
      method,
    ),
  );
  const payingWithGoogleOrApplePay = selectedPaymentMethods.some(({ method }) =>
    [PAYMENT_METHOD.APPLE_PAY, PAYMENT_METHOD.GOOGLE_PAY].includes(method),
  );
  const payingWithCreditCard = selectedPaymentMethods.find(
    method => method.method === PAYMENT_METHOD.CREDIT_CARD,
  );

  const effectiveDeliveryAddress = useAppSelector(state => state.desiredDeliveryAddress);

  const deliveryAddress = [
    effectiveDeliveryAddress?.buildingInformation,
    effectiveDeliveryAddress?.formattedAddress,
  ]
    .filter(Boolean)
    .join(', ');

  const bypassDeliveryDetails = useAppSelector(getBypassDeliveryDetails);
  const deliveryEstimateFailed = useAppSelector(state => state.deliveryEstimateFailed);
  const desiredDeliveryAddressNotFound = useAppSelector(
    state => state.desiredDeliveryAddressNotFound,
  );
  const bypassedDetailsFailedOrMissingAddress =
    bypassDeliveryDetails &&
    (desiredDeliveryAddressNotFound || deliveryEstimateFailed || !deliveryAddress);

  const fullName = `${purchaser.name || ''} ${purchaser.familyName || ''}`.trim();

  const includesSavedCard =
    selectedPaymentMethods.filter(method => method.method === PAYMENT_METHOD.SAVED_CARD).length > 0;

  const ccMissing =
    lodash.find(selectedPaymentMethods, ['method', PAYMENT_METHOD.CREDIT_CARD]) &&
    !creditCardComplete;

  const gcMissingOrInvalid =
    lodash.find(selectedPaymentMethods, ['method', PAYMENT_METHOD.GIFT_CARD]) &&
    !availablePaymentMethods.includes(PAYMENT_METHOD.GIFT_CARD);

  const noSelectedPaymentMethods = selectedPaymentMethods.length === 0;

  const mustBeVerifiedMember = !profile?.verified && !guestOrderingAvailable;

  // gatewayType is fixed for the lifetime of this component (set once from config before mount),
  // so these conditional hook calls always execute in the same order across renders.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const tyroData = gatewayType === PAYMENT_GATEWAY_TYPES.TYRO ? useTyro() : null;
  const redcatPayData =
    // eslint-disable-next-line
    gatewayType === PAYMENT_GATEWAY_TYPES.REDCAT_PAY ? useRedcatPayment() : null;
  const {
    submitRedcatPay,
    cardReady: redcatCardReady,
    cardValid: redcatCardValid,
    pollingReference: redcatPollingReference,
    setPollingReference: setRedcatPollingReference,
    idempotencyKey: redcatIdempotencyKey,
    setIdempotencyKey,
    resumePaymentStatusPolling: resumeRedcatPaymentStatusPolling,
  } = redcatPayData ?? {};

  const { track } = useRedcatAnalytics();
  const paymentAnalyticsBase = { purchaseType: 'order' as const, memberId: member?.id };
  const {
    loading: tyroPayFormLoading,
    payRequestReady,
    payFormReady,
    submittingTyroForm,
    submittingOverlay,
    submitTyroPay,
    setTyroPaymentOrderId,
    setExternalDependency,
    showAppleGooglePayBtn,
    setShowAppleGooglePayBtn,
  } = gatewayType === PAYMENT_GATEWAY_TYPES.TYRO && tyroData;

  const handleCheckout = () => {
    dispatch(setCheckoutInProgress(true));
    checkout();
  };

  // This is a patch that we are doing to solve the issue of members making an order with not enough member points.
  // The actual fix will be done after Chatime goes live with MIM. We know the fix, but just to avoid risk at the last moment we are not applying that fix.
  const isPayingWithMemberPoints =
    !isMultiPaymentAllowed &&
    selectedPaymentMethods.length &&
    selectedPaymentMethods[0]?.method === PAYMENT_METHOD.MEMBER_POINTS;

  const memberNotHaveEnoughPoints =
    member && orderTotals && member?.pointsBalance < orderTotals?.pointsPrice * 100;

  const disableBecauseOfMemberPoints =
    isPayingWithMemberPoints && memberNotHaveEnoughPoints ? true : false;

  const deliveryAddressString = DeliveryAddressDto.toShortAddressString(effectiveDeliveryAddress);

  /**
   * Check if its a free order. If it's a free order remove all the existing
   * selected payment methods. So later on handleCheckout() the free
   * paymentMethod gets added to the list.
   */
  useEffect(() => {
    if (freeOrderReadyToSubmit) {
      dispatch(resetSelectedPaymentMethods({}));
    }
  }, [freeOrderReadyToSubmit, dispatch]);

  const checkout = async () => {
    await dispatch(refreshOrderingProviderDetails());
    await dispatch(updateSelectedPaymentMethods({}));

    const { proceedToFinalise } = await dispatch(validate()).unwrap();

    if (proceedToFinalise) {
      try {
        let subPayment = { ...payment_gateway };
        if (freeOrderReadyToSubmit) {
          dispatch(
            addSelectedPaymentMethod({
              method: PAYMENT_METHOD.FREE,
              amount: 0,
            }),
          );
        } else {
          // We make the payment to TYRO using submitPay
          if (gatewayType === PAYMENT_GATEWAY_TYPES.TYRO && payingWithGoogleOrAppleOrCreditCard) {
            const response = await submitTyroPay();
            if (response.status === TYRO_PAYMENT_TOKEN_STATUS.SUCCESS) {
              setTyroPaymentOrderId(response.origin.orderId);
            } else {
              enqueueErrorSnackbar(response.error);
              dispatch(setCheckoutInProgress(false));
            }
          }

          if (
            gatewayType === PAYMENT_GATEWAY_TYPES.REDCAT_PAY &&
            payment_gateway?.method === PAYMENT_METHOD.CREDIT_CARD
          ) {
            if (redcatPollingReference) {
              // A previous attempt timed out but the session is still alive — resume
              // polling on the same reference instead of submitting a new charge.
              resumeRedcatPaymentStatusPolling?.();
              return;
            }
            const submitted = submitRedcatPay?.();
            if (!submitted) {
              dispatch(setCheckoutInProgress(false));
            }
            return;
          }

          if (
            gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE &&
            payment_gateway?.method === PAYMENT_METHOD.CREDIT_CARD
          ) {
            const cardElement = elements?.getElement(CardElement);

            let token = '';
            let last4 = '';

            if (enableDynamicPaymentGatewayConfig) {
              const { paymentMethod, error } = await stripe!.createPaymentMethod({
                type: 'card',
                card: cardElement!,
                billing_details: {
                  name: fullName,
                  email: purchaser.email!,
                  phone: purchaser.mobile!,
                },
              });

              if (error) {
                throw error?.message;
              }

              token = paymentMethod?.id || '';
              last4 = paymentMethod?.card?.last4 || '';
            } else {
              const { token: cardToken, error } = await stripe!.createToken(cardElement!, {
                name: fullName,
              });

              if (error) {
                throw error?.message;
              }

              token = cardToken?.id || '';
              last4 = cardToken?.card?.last4 || '';
            }

            subPayment.token = token;
            subPayment.lastFour = last4;
          }

          if (payment_gateway?.method === PAYMENT_METHOD.SAVED_CARD) {
            subPayment.token = currentSavedCardToken!;
          }
        }

        if (
          payment_gateway?.method === PAYMENT_METHOD.CREDIT_CARD ||
          payment_gateway?.method === PAYMENT_METHOD.SAVED_CARD
        ) {
          //update payment method with token details
          dispatch(
            applyTokenToGatewayPaymentMethod({
              token: subPayment.token,
              lastFour: subPayment.lastFour,
            }),
          );
        }

        if (
          gatewayType === PAYMENT_GATEWAY_TYPES.REDCAT_PAY &&
          payment_gateway?.method === PAYMENT_METHOD.SAVED_CARD
        ) {
          if (redcatPollingReference) {
            // A previous attempt timed out but the session is still alive — resume
            // polling on the same reference instead of firing a new charge.
            resumeRedcatPaymentStatusPolling?.();
            return;
          }
          try {
            trackPaymentInitiated(track, paymentAnalyticsBase);
            const result = await dispatch(
              chargeRedcatPaySavedCard({
                cardToken: currentSavedCardToken!,
                amount: Math.round(orderTotals?.discountedMoneyPrice ?? 0),
                storeId: locationId ?? undefined,
                receiptEmail: purchaser.email ?? undefined,
                channel: 'web',
                idempotencyKey: redcatIdempotencyKey,
              }),
            ).unwrap();

            if (result.idempotency_key) setIdempotencyKey?.(result.idempotency_key);
            setRedcatPollingReference?.(result.receipt_id);
          } catch (err: any) {
            trackPaymentFailed(track, {
              ...paymentAnalyticsBase,
              failureReason: 'charge_request_failed',
            });
            enqueueErrorSnackbar(err.message || err);
            dispatch(setCheckoutInProgress(false));
          }
          return;
        }

        // We will use this sale api for the BE to do the payment if it's stripe or for tyro if they are paying not paying with google/apple/credit card
        if (gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE || !payingWithGoogleOrAppleOrCreditCard) {
          const response = (await dispatch(
            sale({
              route: 'checkout',
              authenticationMethod: member ? 'member' : 'none',
              deliveryDropOffCoordinates: effectiveDeliveryAddress?.deliveryDropOffCoordinates,
              customAddress: deliveryAddressString,
              customSuburb: effectiveDeliveryAddress?.addressComponents?.suburb,
              customPostCode: effectiveDeliveryAddress?.addressComponents?.postCode,
            }),
          ).unwrap()) as ApiResponse;

          if (response.success) {
            checkoutSuccessful(
              processId(
                response.data.TransactionID || response.data.ID || response.data.transactionId,
              ),
            );
          } else {
            if (response.error_code === 206) {
              handle3ds(response.additional_info);
            } else {
              enqueueErrorSnackbar(response.error);
              dispatch(setCheckoutInProgress(false));
            }
          }
        }
      } catch (err: any) {
        enqueueErrorSnackbar(err.e || err.message);
        dispatch(setCheckoutInProgress(false));
      }
    } else {
      dispatch(setCheckoutInProgress(false));
    }
  };

  const handle3ds = async (additional_info: any) => {
    if (!paymentGatewayConfig) {
      enqueueErrorSnackbar(
        'Missing paymentGatewayConfig. Please check payment gateway has been configured',
      );
      dispatch(setCheckoutInProgress(false));
    }

    // NOTE: the backend clones cc payments to the proper merchant accounts, this is a stripe-intended hacky workaround to get credit card details to save across multiple merchant accounts
    // this is why we make a new stripe instance with the specific merchant account here to do 3ds validation, as 3ds will use the cloned payment within the merchant account, not the one we sent to the main stripe account
    const stripe2 = await loadStripe(paymentGatewayConfig!.clientKey, {
      stripeAccount: paymentGatewayConfig?.merchantAccount,
    });

    const result = await stripe2?.handleCardAction(additional_info.action.client_secret);

    if (result) {
      const { paymentIntent, error } = result;

      if (error) {
        enqueueErrorSnackbar(error.message || '3DS Failed');
        dispatch(setCheckoutInProgress(false));

        return;
      }

      try {
        const finaliseResponse = (await dispatch(
          finaliseSale({
            additional_data: {
              orderInfo: additional_info.orderInfo,
              actionResponse: {
                payment_intent_id: paymentIntent?.id,
              },
            },
            authenticationMethod: member ? 'member' : 'none',
          }),
        ).unwrap()) as ApiResponse;

        const finaliseSaleFailed =
          finaliseResponse.meta && finaliseResponse.meta.requestStatus === 'rejected';

        if (finaliseSaleFailed) {
          enqueueErrorSnackbar(
            finaliseResponse.payload.e ||
              finaliseResponse.payload.e.message ||
              finaliseResponse.payload.reason,
          );
        } else {
          checkoutSuccessful(
            processId(
              finaliseResponse.TransactionID ||
                finaliseResponse.ID ||
                finaliseResponse.transactionId,
            ),
          );
        }
      } catch (e: any) {
        enqueueErrorSnackbar(e.e.message || 'Finalise sale request failed with an unknown error');
        dispatch(setCheckoutInProgress(false));
        return;
      }
    }
  };

  const displayAppleGooglePayBtn =
    orderReadyToSubmit &&
    !gcMissingOrInvalid &&
    (gatewayType === PAYMENT_GATEWAY_TYPES.TYRO
      ? showAppleGooglePayBtn
      : payingWithGoogleOrApplePay);

  const standardButton = (
    // This should be the standard payment
    <StandardButton
      themeKey="checkoutButton"
      label={
        gatewayType === PAYMENT_GATEWAY_TYPES.TYRO && payingWithGoogleOrApplePay
          ? t('button.continue')
          : t('button.checkout')
      }
      onClick={() => {
        if (gatewayType === PAYMENT_GATEWAY_TYPES.TYRO && payingWithGoogleOrApplePay) {
          setExternalDependency({
            showAppleGooglePayBtn,
          });
          setShowAppleGooglePayBtn(true);
        } else {
          handleCheckout();
        }
      }}
      disabled={
        maxValueReached ||
        checkoutInProgress ||
        gcMissingOrInvalid ||
        !orderReadyToSubmit ||
        mustBeVerifiedMember ||
        (freeOrderReadyToSubmit ? false : noSelectedPaymentMethods) ||
        (gatewayType === PAYMENT_GATEWAY_TYPES.TYRO
          ? !payingWithGoogleOrApplePay && !totalCoveredBySubPayments
          : !totalCoveredBySubPayments) ||
        cartContainsSoldOutItems ||
        //@ts-ignore
        cartContainsInvalidQuantities.cartContainsInvalidQuantities ||
        maxOrderValueReached ||
        bypassedDetailsFailedOrMissingAddress ||
        disableBecauseOfMemberPoints ||
        (gatewayType === PAYMENT_GATEWAY_TYPES.STRIPE &&
          (ccMissing || (includesSavedCard && enableCvcCheck && !cvcFilled))) ||
        (gatewayType === PAYMENT_GATEWAY_TYPES.TYRO &&
          payingWithCreditCard &&
          (tyroPayFormLoading ||
            submittingTyroForm ||
            submittingOverlay ||
            !payRequestReady ||
            !payFormReady ||
            paymentStatusPolling)) ||
        (gatewayType === PAYMENT_GATEWAY_TYPES.REDCAT_PAY &&
          payingWithCreditCard &&
          (!redcatCardReady || !redcatCardValid))
      }
      showSpinner={checkoutInProgress}
      containerStyle={combineStyles(styles.button, containerStyle)}
      LeftIconComponent={
        invalidOfferPresent || invalidStagedPurchasePresent ? RiAlertFill : undefined
      }
    />
  );

  const paymentRequestButton = (
    <div style={styles.paymentRequestButtonContainer}>
      {/* This should be apple pay or google pay stripe elements to show the options */}
      <PaymentRequestButton
        containerStyle={combineStyles(styles.button, containerStyle)}
        LeftIconComponent={
          invalidOfferPresent || invalidStagedPurchasePresent ? RiAlertFill : undefined
        }
        paymentStatusPolling={paymentStatusPolling}
      />
    </div>
  );

  if (gatewayType === PAYMENT_GATEWAY_TYPES.TYRO && payingWithGoogleOrApplePay) {
    return (
      <>
        <div style={{ display: displayAppleGooglePayBtn ? 'flex' : 'none' }}>
          {paymentRequestButton}
        </div>
        <div style={{ display: displayAppleGooglePayBtn ? 'none' : 'block' }}>{standardButton}</div>
      </>
    );
  }

  return displayAppleGooglePayBtn ? paymentRequestButton : standardButton;
};

const styles: Styles = {
  button: {
    marginTop: 20,
  },
  paymentRequestButtonContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
};

export default CheckoutButton;
